"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        {[0, 1, 2, 3].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-mono text-xs uppercase tracking-[0.25em] text-foreground font-medium relative group px-1 py-2 bg-transparent hover:bg-transparent focus:bg-transparent">
                <span>{item.label}</span>
                <span className="absolute left-0 bottom-1 h-[1px] w-0 bg-foreground transition-all duration-200 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 p-6">
        {[0, 1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-1 p-6">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id}>
          <Link to={item.href || "#"} data-index={i} className="font-mono text-sm uppercase tracking-[0.25em] text-foreground py-4 px-3 border-b-[1px] border-border hover:border-foreground hover:bg-accent/30 transition-colors">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1535" id="main-deck-header" className={`sticky top-0 z-50 w-full border-b-[1px] border-border transition-all duration-250 ${scrolled ? "bg-background/90 backdrop-blur-sm" : "bg-background"}`}>
      <div className="mx-auto max-w-[1440px] px-5 md:px-8">
        <div className="grid grid-cols-12 items-center h-16 md:h-[76px] gap-4">
          <div className="col-span-8 flex items-center gap-8 lg:gap-12">
            <Link to="/" className="flex items-center shrink-0">
              <WvcLogo className="h-8 w-auto" />
            </Link>

            <div className="hidden lg:flex items-center">
              <MenuProvider menu_id="51" className="header-menu">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          <div className="col-span-4 flex items-center justify-end gap-2 md:gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Search products" className="border-[1px] border-border bg-background hover:bg-accent text-foreground transition-all h-10 w-10">
                  <Search className="size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={12} className="w-80 border-[1px] border-border bg-background p-4">
                <div className="flex flex-col gap-3">
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-foreground">
                    Search the Catch
                  </span>
                  <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search seafood..." />
                </div>
              </PopoverContent>
            </Popover>

            <div className="hidden md:flex items-center">
              <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-foreground border-[1px] border-border px-3 py-1.5">
                EST. 1965
              </div>
            </div>

            <CartIcon variant="ghost" size="icon" iconSize="size-4" className="border-[1px] border-border bg-background hover:bg-accent text-foreground transition-all h-10 w-10 flex items-center justify-center" />

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="lg:hidden border-[1px] border-border bg-background hover:bg-accent text-foreground transition-all h-10 w-10">
                  <Menu className="size-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l-[1px] border-border p-0">
                <SheetHeader className="border-b-[1px] border-border p-6 flex flex-row items-center justify-between">
                  <SheetTitle className="font-serif text-xl uppercase tracking-tight text-foreground">
                    Navigation
                  </SheetTitle>
                </SheetHeader>
                <div className="flex-1 overflow-y-auto">
                  <div className="px-6 pt-4">
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                      Logbook // Index
                    </span>
                  </div>
                  <MenuProvider menu_id="51" className="mobile-header-menu">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}